import React, { useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectAllEmployees } from '_redux/slices/timeline/employees';
import { makeSelectPaxAtLocation } from '_redux/slices/timeline/timeline-areas';
import { selectSelectedTasksList, setSelectedTaskList } from '_redux/slices/timeline/selected-task';
import { setClicked, setClickedFromLocTab, setClickedJob, setPoints } from '_redux/slices/utils/menu-context';
import { Spacing, Colors } from '_styles';
import { ElapsedTime, getIndicatorColor, CountdownTime } from '_components/atoms';
import { processName, handleNotesText, formatTimeLineDashboardAssistance, getAssistanceArray } from '_utils/functions';
import { PiNotepadFill } from 'react-icons/pi';
import { FaPause } from 'react-icons/fa6';

// note icon 
const NoteCell = ({ booking, usedTheme }) => {
    const [hovered, setHovered] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    const notesContent = useMemo(() => booking ? handleNotesText(booking, 'tooltip') : null, [booking]);
    const hasNote = !!notesContent && notesContent !== 'Not Set';

    if (!hasNote) return <td style={tdStyles(usedTheme)} />;

    return (
        <td style={{ ...tdStyles(usedTheme), textAlign: 'center' }}>
            <div
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                onMouseEnter={(e) => { setHovered(true); setPos({ x: e.clientX, y: e.clientY }); }}
                onMouseLeave={() => setHovered(false)}
                onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}
            >
                <PiNotepadFill color={Colors.GRAY_DARK} size={14} />
            </div>

            {hovered && createPortal(
                <div style={{
                    position: 'fixed',
                    left: pos.x + 12,
                    top: pos.y + 12,
                    backgroundColor: 'white',
                    border: `1px solid ${Colors.GRAY_LIGHT || '#e5e7eb'}`,
                    borderRadius: 8,
                    padding: Spacing.SCALE_8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 2147483647,
                    maxWidth: 260,
                    whiteSpace: 'pre-line',
                    fontSize: 12,
                    color: Colors.TEXT_DARK || '#111827',
                    pointerEvents: 'none',
                }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: Colors.PRM_BLUE || '#1e40af', fontSize: 11 }}>
                        Notes
                    </div>
                    {notesContent}
                </div>,
                document.body
            )}
        </td>
    );
};

// ssr code 
const SsrCell = ({ booking, usedTheme }) => {
    const [hovered, setHovered] = useState(false);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    const assistance = booking?.assistance ?? booking?.passengerInformation?.assistance;
    const allCodes = useMemo(() => {
        if (!assistance) return [];
        try { return getAssistanceArray(assistance) ?? []; } catch { return []; }
    }, [assistance]);

    const displayText = useMemo(() => formatTimeLineDashboardAssistance(assistance), [assistance]);
    const hasOverflow = allCodes.length > 1;

    if (!allCodes.length) return <td style={tdStyles(usedTheme)}>—</td>;

    return (
        <td style={{ ...tdStyles(usedTheme), fontWeight: 500, letterSpacing: '0.2px' }}>
            <span
                style={{ cursor: hasOverflow ? 'default' : 'default', whiteSpace: 'nowrap' }}
                onMouseEnter={(e) => { if (hasOverflow) { setHovered(true); setPos({ x: e.clientX, y: e.clientY }); } }}
                onMouseLeave={() => setHovered(false)}
                onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}
            >
                {displayText}
            </span>

            {hovered && hasOverflow && createPortal(
                <div style={{
                    position: 'fixed',
                    left: pos.x + 12,
                    top: pos.y + 12,
                    backgroundColor: 'white',
                    border: `1px solid ${Colors.GRAY_LIGHT || '#e5e7eb'}`,
                    borderRadius: 8,
                    padding: Spacing.SCALE_8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 2147483647,
                    minWidth: 80,
                    pointerEvents: 'none',
                }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: Colors.PRM_BLUE || '#1e40af', fontSize: 11 }}>
                        Assistance
                    </div>
                    {allCodes.map((code, i) => (
                        <div key={i} style={{ fontSize: 12, color: Colors.TEXT_DARK || '#111827', lineHeight: 1.6 }}>
                            {code}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </td>
    );
};

// ── Main component 
const LocationDetailView = ({ locationId, usedTheme, sortMode = 'flight' }) => {
    const dispatch = useDispatch();
    const selectPax = useMemo(() => makeSelectPaxAtLocation(locationId), [locationId]);
    const paxList = useSelector(selectPax);
    const selectedTasksList = useSelector(selectSelectedTasksList);
    const allEmployees = useSelector(selectAllEmployees);
    const [hoveredRowId, setHoveredRowId] = useState(null);

    const selectedIds = useMemo(() => {
        return Array.isArray(selectedTasksList) ? selectedTasksList.map(t => t?._id) : [];
    }, [selectedTasksList]);

    const isRC = useCallback((row) => !!(row.booking?.paxManualCheckIn || row.booking?.originalBookingDoc?.paxManualCheckIn), []); // check if the pax is remote checked-in

    const sortedPaxList = useMemo(() => {
        if (!paxList?.length) return [];
        const list = [...paxList];
        const isRC = (row) => !!(row.booking?.paxManualCheckIn || row.booking?.originalBookingDoc?.paxManualCheckIn);
        if (sortMode === 'waiting') {
            return list.sort((a, b) => {
                if (isRC(a) !== isRC(b)) return isRC(a) ? -1 : 1;
                const aTime = a.checkedInTime ? new Date(a.checkedInTime).getTime() : Infinity;
                const bTime = b.checkedInTime ? new Date(b.checkedInTime).getTime() : Infinity;
                return aTime - bTime;
            });
        }
        return list.sort((a, b) => {
            if (isRC(a) !== isRC(b)) return isRC(a) ? -1 : 1;
            const aTime = a.flightTime ? new Date(a.flightTime).getTime() : Infinity;
            const bTime = b.flightTime ? new Date(b.flightTime).getTime() : Infinity;
            return aTime - bTime;
        });
    }, [paxList, sortMode]);

    const handleRowClick = useCallback((row) => {
        if (!row?.booking) return;
        const booking = row.booking;
        if (selectedIds.includes(booking._id)) {
            dispatch(setSelectedTaskList(selectedTasksList.filter(t => t?._id !== booking._id)));
        } else {
            dispatch(setSelectedTaskList([...selectedTasksList, booking]));
        }
    }, [selectedIds, selectedTasksList, dispatch]);

    const handleLeftClick = useCallback((e, job) => {

        console.log('job', job);
        
        if (!job) return;
        // check if the job is break task and completed don't open context menu
        if (job.completed && job?.isBreakTask) return;
        if (job._id?.startsWith("CONNECT-") || job.isConnectBooking) return;
        e.preventDefault();
        e.stopPropagation();

        // open context menu
        dispatch(setClicked(true));
        dispatch(setPoints({ x: e.pageX, y: e.pageY }));
        dispatch(setClickedJob(job.booking));
        dispatch(setClickedFromLocTab(true));

    }, [locationId, dispatch]);



    const getEmployeeName = (row) => {
        if (row.employeeName && row.employeeName !== "MISSING") return row.employeeName;
        if (row.employeeName === "MISSING") {
            const emp = allEmployees?.find(employee => employee.employeeId === row.employeeId);
            return emp?.title || emp?.name || row.employeeName;
        }
        return "Unassigned";
    }

    if (!paxList?.length) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: Spacing.SCALE_20, opacity: 0.5, fontSize: '13px' }}>
                No passengers at this location
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={thStyles(usedTheme)}>Passenger</th>
                        <th style={thStyles(usedTheme)}>SSR</th>
                        <th style={{ ...thStyles(usedTheme), textAlign: 'center', width: 32 }}>Note</th>
                        <th style={thStyles(usedTheme)}>Flight</th>
                        <th style={thStyles(usedTheme)}>Departure Time</th>
                        <th style={thStyles(usedTheme)}>Waiting Time</th>
                        <th style={thStyles(usedTheme)}>Employee</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedPaxList.map((row, i) => {
                        const isSelected = selectedIds.includes(row._id);
                        const isHovered = hoveredRowId === i;
                        const rowIsRC = isRC(row);
                        const rowBg = isSelected || isHovered
                            ? (usedTheme?.SELECTED_COLOR || '#e0f2fe')
                            : rowIsRC ? '#f3eeff'
                            : 'transparent';

                        return (
                            <tr
                                key={i}
                                onClick={() => handleRowClick(row)}
                                onContextMenu={(e) => handleLeftClick(e, row)}
                                onMouseEnter={() => setHoveredRowId(i)}
                                onMouseLeave={() => setHoveredRowId(null)}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: rowBg,
                                    transition: 'background-color 0.15s ease',
                                    boxShadow: rowIsRC ? 'inset 3px 0 0 #6F42C1' : 'none',
                                }}
                            >
                                <td style={tdStyles(usedTheme)}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {processName(row.name)}
                                        {rowIsRC && (
                                            <span style={{
                                                fontSize: 9,
                                                fontWeight: 700,
                                                color: '#fff',
                                                background: '#6F42C1',
                                                borderRadius: 3,
                                                padding: '1px 4px',
                                                letterSpacing: 0.3,
                                                flexShrink: 0,
                                            }}>RC</span>
                                        )}
                                    </span>
                                </td>
                                <SsrCell booking={row.booking} usedTheme={usedTheme} />
                                <NoteCell booking={row.booking} usedTheme={usedTheme} />
                                <td style={tdStyles(usedTheme)}>
                                    {row.flightNumber}{row.destination ? ` (${row.destination})` : ''}
                                </td>
                                <td style={tdStyles(usedTheme)}>
                                    <CountdownTime until={row?.flightTime} />
                                </td>
                                <td style={tdStyles(usedTheme)}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        <ElapsedTime since={row.checkedInTime} frozenAt={row.checkInPausedAt} />
                                        {row.checkInPausedAt && <FaPause size={10} color="#f97316" title="Check-in time paused" />}
                                    </span>
                                </td>

                                <td style={{ ...tdStyles(usedTheme), opacity: row.assigned ? 1 : 0.4 }}>
                                    {/* {row.employeeName || 'Unassigned'} */}
                                    {getEmployeeName(row)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const thStyles = (usedTheme) => ({
    padding: `${Spacing.SCALE_4}px ${Spacing.SCALE_8}px`,
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    borderBottom: `1px solid ${usedTheme?.BORDER_COLOR || 'lightgrey'}`,
    whiteSpace: 'nowrap',
});

const tdStyles = (usedTheme) => ({
    padding: `${Spacing.SCALE_8}px ${Spacing.SCALE_8}px`,
    fontSize: '12px',
    borderBottom: `1px solid ${usedTheme?.BORDER_COLOR || 'lightgrey'}`,
    whiteSpace: 'nowrap',
});

export default LocationDetailView;
